import { type GridApi, ModuleRegistry } from 'ag-grid-community'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import pluralize from 'pluralize'
import { useCallback, useMemo } from 'react'

import { Artifact } from '@/gen/artifacts/v1/model_pb'

import { useAgGridSelectionChanged } from '@/lib/ag-grid/ag-grid-selection-changed.hook'
import { cn } from '@/lib/ui/style-helpers'

import { ARTIFACT_EMPTY_STATE_LABELS } from '@/pages/company-drawer/artifact/labels'

import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { AgCollapseChildrenTable } from '@/components/lemmala-design-system/table/ag-collapse-children-table'
import { TABLE_ROW_HEIGHTS } from '@/components/lemmala-design-system/table/consts'
import { TABLE_EMPTY_STATE_LABELS } from '@/components/lemmala-design-system/table/labels'
import type {
  GroupingMetadata,
  RowWithGroupingMetadata,
  TableEmptyStateDefinition,
} from '@/components/lemmala-design-system/table/types'
import { clearFiltersAction } from '@/components/lemmala-design-system/table/utils/table-empty-state'

import { getArtifactColumnDefs } from './config/column-defs'
import { groupArtifactsByVersion } from './utils/group-artifacts-by-version'

// Register AG Grid modules
ModuleRegistry.registerModules([AllEnterpriseModule])

/**
 * Which rows a checkbox may appear on -- everything except the synthetic
 * "3 previous versions" header, which reuses its parent's data under a derived
 * id that resolves to nothing server-side.
 *
 * Previous versions are selectable, and select-all takes them too. They are only
 * rows at all while their stack is expanded (see buildGroupedRows), so anything
 * select-all can reach is on screen.
 */
const isSelectableRow = (row: GroupingMetadata): boolean => !row.isGroupHeader

const ARTIFACT_EMPTY_STATE: TableEmptyStateDefinition = {
  filtered: {
    title: ARTIFACT_EMPTY_STATE_LABELS.filteredTitle,
    description: TABLE_EMPTY_STATE_LABELS.filteredDescription,
    button: clearFiltersAction,
  },
}

export type AgArtifactTableProps = {
  data: Artifact[]
  isLoading?: boolean
  readOnly?: boolean
  isExport?: boolean
  /** Render at natural height with page-level scrolling; only safe for small datasets. */
  autoHeight?: boolean
  className?: string
  /** Ignored when `isExport` or `autoHeight` is set (those modes manage their own sizing). */
  containerClassName: string
  /** Context identifier to scope filters (e.g., 'companyDrawer', 'evidenceCollection') */
  filterContext?: string
  selection?: {
    enabled: boolean
    onSelectionChanged?: (ids: string[]) => void
  }
  onGridReady?: (api: GridApi) => void
}

export const AgArtifactTable = ({
  data,
  isLoading = false,
  readOnly = false,
  isExport = false,
  autoHeight = false,
  className,
  containerClassName,
  filterContext,
  selection,
  onGridReady,
}: AgArtifactTableProps) => {
  const artifactSources = useMemo(() => {
    return data.map((artifact) => artifact.source)
  }, [data])

  const columnDefs = useMemo(
    () => getArtifactColumnDefs(data, artifactSources, readOnly, isExport),
    [data, artifactSources, readOnly, isExport],
  )

  const doesRowMatch = useCallback((row: Artifact, query: string) => {
    const searchText = query.toLowerCase().trim()
    if (searchText.length === 0) return true
    const haystack = `${row.artifactSubcategory || ''} ${row.name || ''}`.toLowerCase()
    return haystack.includes(searchText)
  }, [])

  const getSelectedIds = useCallback(
    (rows: RowWithGroupingMetadata<Artifact>[]) => rows.filter(isSelectableRow).map((r) => r.id),
    [],
  )
  const onSelectionChanged = useAgGridSelectionChanged({ selection, getSelectedIds })

  const gridOptions = useMemo(
    () => ({
      rowHeight: TABLE_ROW_HEIGHTS.single,
      suppressHorizontalScroll: isExport,
      rowClass: '',
      onSelectionChanged,
    }),
    [isExport, onSelectionChanged],
  )

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='text-main-foreground-tertiary'>Loading artifacts...</div>
      </div>
    )
  }

  const shouldUseAutoHeight = !isExport && autoHeight
  if (data.length === 0) {
    return (
      <Empty
        icon='secondary'
        title={ARTIFACT_EMPTY_STATE_LABELS.zeroDataTitle}
        className='w-full rounded border bg-main-background-card py-12'
      />
    )
  }

  return (
    <div className={className}>
      <AgCollapseChildrenTable<Artifact>
        data={data}
        toGroups={groupArtifactsByVersion}
        groupHeader={{
          getLabel: (row) => {
            const name = row.artifactSubcategory || 'artifact'
            return `${row.groupChildCount} previous ${name} ${pluralize('version', row.groupChildCount)}`
          },
        }}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        onGridInit={(event) => {
          const state = event.api.getColumnState()
          const hasSort = state?.some((c) => c.sort)
          if (!hasSort) {
            event.api.applyColumnState({
              state: [{ colId: 'dateAdded', sort: 'asc' }],
            })
          }
        }}
        search={{ doesRowMatch }}
        hideSearch={readOnly}
        urlSync={{
          enabled: !isExport && !!filterContext,
          prefix: filterContext,
        }}
        filters={{
          defaultVisibleFilters: ['artifactSubcategory', 'source', 'analysisStatus'],
        }}
        selection={
          selection?.enabled
            ? {
                mode: 'multiple',
                checkboxes: true,
                isRowSelectable: (node) => isSelectableRow(node.data ?? {}),
                hideDisabledCheckboxes: true,
              }
            : undefined
        }
        onGridReady={onGridReady}
        hideExport
        hideCount={readOnly}
        domLayout={isExport ? 'print' : shouldUseAutoHeight ? 'autoHeight' : 'normal'}
        className={cn({ '!h-auto min-h-36 overflow-visible': shouldUseAutoHeight })}
        tableClassName={cn({ '!h-auto': shouldUseAutoHeight })}
        entityName='artifact'
        emptyStateDefinition={ARTIFACT_EMPTY_STATE}
        containerClassName={isExport || shouldUseAutoHeight ? undefined : containerClassName}
      />
    </div>
  )
}
